<template>
  <card-dialog
    v-model="modalVisible"
    fullscreen
    persistent
    prevent-close
    @close="anyProgressMade ? closeConfirmationDialogVisible = true : modalVisible = false"
  >
    <template v-slot:title>
      <v-card-title class="grey-bg px-6 py-3">
        <div v-if="protocolFormData.id || editItems.length > 0">
          {{ $t('card_title.edit_training_protocol') }}
        </div>
        <div v-else>
          {{ $t('training_builder.dialog.header') }}
        </div>
      </v-card-title>
    </template>
    <v-card-text
      class="pa-0"
    >
      <validation-observer ref="validation">
        <v-row
          class="fill-height"
          no-gutters
        >
          <v-col
            cols="4"
            class="fill-height overflow-auto pa-6"
          >
            <validation-provider
              v-if="editItems.length === 0 || (editTitle && editTitleAllowed)"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="protocolFormData.name"
                :error-messages="errors"
                :label="$t('text_input.protocol_name.title')"
                outlined
                dense
                :disabled="isReadOnly"
                :placeholder="$t('training_builder.text_input.protocol_name.placeholder')"
              />
            </validation-provider>
            <subtitle
              v-else-if="editTitle && editItems.length > 0"
              class="font-weight-medium"
            >
              {{ editTitle }}
            </subtitle>

            <validation-provider
              v-if="editItems.length === 0 || (editDescription && editDescriptionAllowed)"
              v-slot="{ errors }"
              rules="required"
            >
              <v-textarea
                v-model="protocolFormData.description"
                :error-messages="errors"
                :label="$t('training_builder.text_input.description.title')"
                outlined
                rows="3"
                dense
                :disabled="isReadOnly"
                :placeholder="$t('training_builder.text_input.description.placeholder')"
              />
            </validation-provider>
            <v-card
              v-else-if="editDescription && editItems.length > 0"
              outlined
              class="mt-1 pa-3 grey--text text--darken-2 text-body-1 "
            >
              {{ editDescription }}
            </v-card>

            <div v-if="!listenEvents && editItems.length === 0 && !isReadOnly">
              <v-switch
                v-model="saveAsTemplate"
                :label="$t('training_programs.dialog.save_as_template')"
                dense
                class="ma-0 pa-0"
              />
            </div>

            <div v-if="$can('create-shared-training-protocols') && (listenEvents || saveAsTemplate)">
              <v-switch
                v-model="protocolFormData.is_shared"
                :label="$t('switch.share_across_organisation')"
                dense
                class="ma-0 pa-0"
              />
            </div>

            <div
              class="white mx-n3 px-3 py-3"
              style="position: sticky; top: -25px; z-index: 50"
            >
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  <subtitle>
                    {{ $t('training_builder.exercises.title') }}
                  </subtitle>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    color="grey darken-2"
                    class="text-none mr-1"
                    small
                    text
                    :disabled="isReadOnly"
                    @click="addSuperset"
                  >
                    <v-icon
                      x-small
                      left
                    >
                      fal fa-plus
                    </v-icon>
                    {{ $t('training_builder.button.add_superset') }}
                  </v-btn>
                  <v-btn
                    color="grey darken-2"
                    class="text-none mr-1"
                    small
                    text
                    :disabled="isReadOnly"
                    @click="addGroup"
                  >
                    <v-icon
                      x-small
                      left
                    >
                      fal fa-plus
                    </v-icon>
                    {{ $t('training_builder.button.add_section') }}
                  </v-btn>
                  <v-btn
                    color="grey darken-2"
                    class="text-none"
                    small
                    text
                    :disabled="isReadOnly"
                    @click="addPause"
                  >
                    <v-icon
                      x-small
                      left
                    >
                      fal fa-plus
                    </v-icon>
                    {{ $t('training_builder.button.add_pause') }}
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <v-card
              v-if="items.length === 0"
              color="grey lighten-4"
              rounded="lg"
              flat
            >
              <v-card-text class="text-center py-6 grey--text">
                <div class="mb-4">
                  <v-icon
                    large
                    color="grey lighten-1"
                  >
                    fal fa-circle-plus
                  </v-icon>
                </div>
                <div>{{ $t('training_builder.add_exercises_box.placeholder') }}</div>
              </v-card-text>
            </v-card>
            <div v-else>
              <draggable
                :list="items"
                handle=".handle"
                :group="{ name: 'exercises' }"
                :invert-swap="true"
                :swap-treshold="0.6"
                :animation="100"
                :move="checkDragDrop"
                :scroll="true"
                :scroll-sensitivity="100"
                :scroll-speed="10"
                :force-fallback="true"
                :disabled="isReadOnly"
              >
                <div
                  v-for="(item, index) in items"
                  :key="item.internal_id"
                  class="mb-3"
                >
                  <superset-selected-card
                    v-if="item.type === 'superset'"
                    :item.sync="item"
                    :player="player"
                    :players="selectablePlayers"
                    :history-allowed="historyAllowed"
                    :editable="!isReadOnly"
                    @delete="items.splice(index, 1)"
                  />
                  <group-selected-card
                    v-else-if="item.type === 'group'"
                    :item.sync="item"
                    :player="player"
                    :players="selectablePlayers"
                    :history-allowed="historyAllowed"
                    :editable="!isReadOnly"
                    @delete="items.splice(index, 1)"
                    @saved="fetchUserGroups()"
                  />
                  <pause-selected-card
                    v-if="item.type === 'pause'"
                    :item.sync="item"
                    :player="player"
                    :history-allowed="historyAllowed"
                    @delete="items.splice(index, 1)"
                  />
                  <exercise-selected-card-template
                    v-else-if="item.group"
                    :exercise="item"
                    :player="player"
                    :players="selectablePlayers"
                    :history-allowed="historyAllowed"
                    :editable="!isReadOnly"
                    @delete="items.splice(index, 1)"
                  />
                </div>
              </draggable>
            </div>

            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <input
                type="hidden"
                :value="items.length > 0 ? true : undefined"
              >
              <div
                v-if="errors.length > 0"
                class="error--text my-4 text-body-1"
              >
                {{ $t("training_builder.exercise_required") }}
              </div>
            </validation-provider>
          </v-col>
          <v-divider
            vertical
          />
          <v-col class="fill-height pa-6 pb-0">
            <div class="d-flex flex-column no-gutters fill-height">
              <div class="col col-auto pa-0">
                <v-row
                  no-gutters
                  align="center"
                >
                  <v-col>
                    <subtitle>
                      {{ $t('training_builder.exercise_library.title') }}
                    </subtitle>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      elevation="0"
                      :color="filters.groupId === 'groups' ? 'primary' : 'grey lighten-4'"
                      class="text-none mr-4"
                      dense
                      @click="handleGroupFilter('groups')"
                    >
                      {{ $t('training_builder.groups.saved') }}
                    </v-btn>
                    <v-btn
                      v-for="group in groups.data"
                      :key="'group'+group.id"
                      class="text-none mr-2"
                      elevation="0"
                      :color="filters.groupId === group.id ? 'primary' : 'grey lighten-4'"
                      dense
                      @click="handleGroupFilter(group.id)"
                    >
                      {{ group.name }}
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row
                  class="py-4"
                  no-gutters
                  style="min-height: 72px"
                  align="center"
                >
                  <v-col
                    class="mr-2"
                  >
                    <v-text-field
                      v-model="filters.search"
                      dense
                      outlined
                      hide-details
                      append-icon="fal fa-search"
                      :placeholder="filters.groupId === 'groups' ? $t('training_builder.text_input.search_sections') : $t('training_builder.text_input.search_exercises')"
                      style="max-width: 400px"
                      clearable
                    />
                  </v-col>

                  <template v-if="selectedGroup && selectedGroup.params.tags">
                    <v-col
                      v-for="tag in (selectedGroup.params.tags.filter(t => !t.is_hidden) || [])"
                      :key="'filter'+tag.id"
                      cols="auto"
                      class="mr-2"
                    >
                      <v-select
                        :value="filters.tags[tag.id]"
                        :items="tag.items"
                        :label="tag.label"
                        dense
                        outlined
                        style="width: 140px"
                        clearable
                        hide-details
                        @input="handleFilterTag(tag.id, $event)"
                      />
                    </v-col>
                  </template>
                  <template v-else-if="filters.groupId === 'groups'">
                    <v-col cols="auto">
                      <v-select
                        v-model="filters.groupTags"
                        multiple
                        outlined
                        dense
                        hide-details
                        :placeholder="$t('dropdown.search_by_tags.title')"
                        chips
                        small-chips
                        deletable-chips
                        :items="tags.data"
                        :loading="tags.loading"
                        item-value="id"
                        item-text="label"
                        style="width: 350px"
                        clearable
                        @change="fetchUserGroups()"
                      >
                        <template v-slot:selection="{ item, index }">
                          <span
                            v-if="index === 0"
                            class="text-grey text-body-2 align-self-center"
                          >
                            {{ $tc("dropdown.tags_selected", filters.groupTags.length , {num: filters.groupTags.length }) }}
                          </span>
                        </template>
                      </v-select>
                    </v-col>
                  </template>
                </v-row>
              </div>
              <v-divider />
              <div style="height: 1px">
                <v-progress-linear
                  v-if="exercises.loading || userGroups.loading"
                  indeterminate
                  color="primary"
                  :height="1"
                />
              </div>
              <div>
                <v-row
                  no-gutters
                  align="center"
                  style="min-height: 48px"
                >
                  <v-col
                    v-if="filters.groupId !== 'groups'"
                    cols="auto"
                  >
                    <v-btn-toggle
                      v-model="filters.shareStatus"
                      mandatory
                      color="primary"
                      dense
                      class="rounded-lg mr-4"
                    >
                      <v-btn
                        value="methodology"
                        class="text-none"
                        small
                      >
                        {{ $t('training_builder.filter.methodology_department') }}
                      </v-btn>
                      <v-btn
                        value="custom"
                        class="text-none"
                        small
                      >
                        {{ $t('training_builder.filter.custom') }}
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>
                  <v-col
                    v-if="filters.groupId !== 'groups' && $can('create-shared-training-exercises')"
                    cols="auto"
                  >
                    <v-switch
                      v-model="filters.onlyMyExercises"
                      dense
                      :label="$t('training_builder.switch.only_my_exercises')"
                      class="pa-0 ma-0"
                      hide-details
                    />
                  </v-col>
                  <v-spacer />
                  <v-col
                    cols="auto"
                    class="pt-1"
                  >
                    <v-btn
                      color="primary"
                      text
                      class="text-none"
                      @click="createNewExercise()"
                    >
                      <v-icon
                        x-small
                        left
                      >
                        far fa-plus
                      </v-icon>
                      {{ $t('training_builder.button.create_new_exercise') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="auto">
                    <div
                      v-if="filters.groupId !== 'groups' && exercises.pagination.last_page > 1"
                      class="pt-1"
                    >
                      <pagination
                        v-model="exercises.pagination.current_page"
                        :last-page="exercises.pagination.last_page"
                      />
                    </div>
                    <div
                      v-else-if="filters.groupId === 'groups' && userGroups.pagination.last_page > 1"
                      class="pt-1"
                    >
                      <pagination
                        v-model="userGroups.pagination.current_page"
                        :last-page="userGroups.pagination.last_page"
                      />
                    </div>
                  </v-col>
                </v-row>
              </div>

              <div
                v-if="filters.groupId !== 'groups'"
                class="col overflow-y-auto overflow-x-hidden pa-3 mt-2"
              >
                <div
                  v-if="exercises.data.length === 0 && !exercises.loading"
                  class="py-12 grey--text text-center"
                >
                  {{ $t("training_builder.no_exercises.label") }}
                </div>
                <v-row v-else>
                  <exercise-card
                    v-for="exercise in exercises.data"
                    :key="'library-exercise-'+exercise.id"
                    :exercise="exercise"
                    :last-item-type="lastItemType"
                    :allow-add-to-protocol="!isReadOnly"
                    @add="addExerciseToList(exercise, ...arguments)"
                  />
                </v-row>
              </div>
              <div
                v-else
                class="col overflow-y-auto overflow-x-hidden pa-3 mt-2"
              >
                <div
                  v-if="userGroups.data.length === 0 && !userGroups.data.loading"
                  class="py-12 grey--text text-center"
                >
                  {{ filters.search === '' && Object.keys(filters.groupTags).length === 0 ? $t('training_builder.no_save_sections.label') : $t('training_builder.no_save_sections_match_filter.label') }}
                </div>
                <v-row>
                  <UserExerciseGroupCard
                    v-for="userGroup in userGroups.data"
                    :key="'user-group-'+userGroup.id"
                    :group="userGroup"
                    :allow-add-to-protocol="!isReadOnly"
                    @add="addSavedGroup(userGroup)"
                    @saved="fetchUserGroups()"
                  />
                </v-row>
              </div>
            </div>
          </v-col>
        </v-row>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          color="primary"
          large
          :loading="submitting"
          :disabled="isReadOnly"
          @click="submit()"
        >
          {{ $t("training_builder.button.save") }}
        </v-btn>
        <v-btn
          color="grey"
          text
          @click="anyProgressMade ? closeConfirmationDialogVisible = true : modalVisible = false"
        >
          {{ $t('button.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="protocolFormData.id"
          color="error"
          text
          :disabled="isReadOnly"
          @click="deleteConfirmationDialogVisible = true"
        >
          <v-icon
            left
            x-small
          >
            far fa-trash
          </v-icon>
          {{ $t('button.delete') }}
        </v-btn>
        <v-btn
          v-if="protocolFormData.id"
          color="grey darken-2"
          text
          @click="duplicateProtocol()"
        >
          <v-icon
            left
            x-small
          >
            far fa-copy
          </v-icon>
          {{ $t('button.duplicate') }}
        </v-btn>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteProtocol()"
    />

    <delete-confirmation-dialog
      :visible.sync="closeConfirmationDialogVisible"
      :title="$t('card_dialog.lose_progress.title')"
      :text="$t('card_dialog.lose_progress.label')"
      :cta-text="$t('card_dialog.lose_progress.button')"
      @confirmed="modalVisible = false"
    />

    <v-dialog
      v-model="loadingProtocol"
      persistent
      max-width="600px"
      overlay-opacity="0.8"
      content-class="elevation-0"
    >
      <v-card
        color="transparent"
        class="white--text text-center"
        height="100px"
      >
        <v-progress-circular
          color="white"
          :width="2"
          :size="80"
          indeterminate
        />
      </v-card>
    </v-dialog>
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import Subtitle from '@/components/helpers/Subtitle'
import { cloneDeep, uniqueId } from 'lodash'
import { TrainingExerciseApiService } from '@/data/services/TrainingExerciseApiService'
import { TrainingProtocolApiService } from '@/data/services/TrainingProtocolApiService'
import ExerciseCard from '@/components/training-builder/ExerciseCard'
import draggable from 'vuedraggable'
import SupersetSelectedCard from '@/components/training-builder/SupersetSelectedCard.vue'
import PauseSelectedCard from '@/components/training-builder/PauseSelectedCard.vue'
import GroupSelectedCard from '@/components/training-builder/GroupSelectedCard.vue'
import debounce from 'lodash/debounce'
import UserExerciseGroupCard from '@/components/training-builder/UserExerciseGroupCard.vue'
import Pagination from '@/components/Pagination.vue'
import ExerciseSelectedCardTemplate from '@/components/training-builder/ExerciseSelectedCardTemplate.vue'
import { TagApiService } from '@/data/services/TagApiService'
import i18n from '@/plugins/i18n'

const emptyProtocolFormData = {
  id: undefined,
  name: '',
  description: '',
  is_shared: false,
  created_by_user: undefined
}

export default {
  name: 'TrainingProtocolFormDialog',
  components: { ExerciseSelectedCardTemplate, Pagination, UserExerciseGroupCard, GroupSelectedCard, PauseSelectedCard, SupersetSelectedCard, ExerciseCard, Subtitle, DeleteConfirmationDialog, CardDialog, draggable },
  mixins: [deleteConfirmationMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    editItems: {
      type: Array,
      default: () => ([])
    },
    editTitle: {
      type: String,
      default: undefined
    },
    editDescription: {
      type: String,
      default: undefined
    },
    editTitleAllowed: {
      type: Boolean,
      default: false
    },
    editDescriptionAllowed: {
      type: Boolean,
      default: false
    },
    listenEvents: {
      type: Boolean,
      default: true
    },
    player: {
      type: Object,
      default: () => undefined
    },
    selectablePlayers: {
      type: Array,
      default: () => ([])
    },
    historyAllowed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loadingProtocol: false,
      protocolFormData: cloneDeep(emptyProtocolFormData),
      submitting: false,
      exercises: {
        loading: false,
        data: [],
        pagination: {
          current_page: 1,
          last_page: undefined,
          per_page: 30
        }
      },
      groups: {
        loading: false,
        data: []
      },
      userGroups: {
        loading: false,
        data: [],
        pagination: {
          current_page: 1,
          last_page: undefined,
          per_page: 30
        }
      },
      items: [],
      filters: {
        groupId: undefined,
        tags: {},
        search: '',
        shareStatus: undefined,
        onlyMyExercises: false,
        groupTags: []
      },
      closeConfirmationDialogVisible: false,
      anyProgressMade: false,
      tags: {
        data: [],
        loading: false
      },
      saveAsTemplate: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    selectedGroup() {
      const group = this.groups.data.find(g => g.id === this.filters.groupId)

      if (!group) return undefined

      group.params.tags = group.params.tags.map(tag => {
        if (tag.filter_by) {
          if (this.filters.tags[tag.filter_by]) tag.items = tag.values[this.filters.tags[tag.filter_by]]
          else tag.items = []
        } else {
          tag.items = tag.values
        }

        return tag
      })

      return group
    },
    lastItemType() {
      if (this.items.length === 0) return 'exercise'

      const lastType = this.items[this.items.length - 1].type

      if (lastType === 'superset' || lastType === 'group') {
        if (this.items[this.items.length - 1].exercises.length > 0) return lastType
      }

      return 'exercise'
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    isReadOnly() {
      if (!this.protocolFormData.id) return false

      if (this.$can('create-shared-training-protocols')) return false

      return this.protocolFormData.created_by_user_id !== this.user.id
    }

  },
  watch: {
    visible(newValue) {
      if (!newValue) return

      this.filters.search = ''
      this.filters.shareStatus = this.$can('create-shared-training-exercises') ? 'methodology' : 'custom'
      this.filters.onlyMyExercises = false

      this.fetchAllGroups()
      this.fetchTags()

      setTimeout(() => {
        this.$refs.validation.reset()
      }, 0)

      if (this.editItems.length > 0) {
        this.items = cloneDeep(this.editItems)
        this.assignInternalIds(this.items)

        this.protocolFormData.name = this.editTitle
        this.protocolFormData.description = this.editDescription

        this.$nextTick(() => {
          this.anyProgressMade = false
        })
      } else if (!this.listenEvents) {
        this.items = []
        this.protocolFormData.name = null
        this.protocolFormData.description = null
        this.saveAsTemplate = false

        this.$nextTick(() => {
          this.anyProgressMade = false
        })
      }
    },
    'filters.search': debounce(function () {
      this.fetchExercisesOrGroups()
    }, 500),
    'filters.groupId': function () {
      this.filters.tags = {}

      this.fetchExercisesOrGroups()
    },
    'filters.shareStatus': function () {
      this.fetchExercisesOrGroups()
    },
    'filters.onlyMyExercises': function () {
      this.fetchExercisesOrGroups()
    },
    'exercises.pagination.current_page': function () {
      this.fetchAllExercises()
    },
    'userGroups.pagination.current_page': function () {
      this.fetchUserGroups()
    },
    items: {
      deep: true,
      handler: function (newValue, oldValue) {
        this.anyProgressMade = true
      }
    }
  },
  created() {
    if (this.listenEvents) {
      EventBus.$on(this.$events.OPEN_DIALOG_TRAINING_PROTOCOL_FORM, () => {
        this.modalVisible = true
        this.protocolFormData = cloneDeep(emptyProtocolFormData)
        this.items = []

        if (this.$can('create-shared-training-protocols')) {
          this.protocolFormData.is_shared = true
        }

        this.$nextTick(() => {
          this.anyProgressMade = false
        })
      })

      EventBus.$on(this.$events.EDIT_TRAINING_PROTOCOL, (protocol) => {
        this.fetchProtocol(protocol.id)

        this.modalVisible = true
      })
    }

    EventBus.$on(this.$events.TRAINING_EXERCISE_CREATED, () => this.fetchAllExercises())
  },
  methods: {
    async fetchProtocol(id) {
      try {
        this.loadingProtocol = true

        const result = await TrainingProtocolApiService.getDetails(id)

        this.protocolFormData = {
          id: result.data.id,
          name: result.data.name,
          description: result.data.description,
          is_shared: result.data.is_shared,
          created_by_user_id: result.data.created_by_user_id
        }
        this.items = result.data.exercises
        this.assignInternalIds(this.items)

        this.$nextTick(() => {
          this.anyProgressMade = false
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_exercises.error'),
          color: 'error'
        })
      } finally {
        this.loadingProtocol = false
      }
    },
    fetchExercisesOrGroups() {
      if (this.filters.groupId === undefined) return

      if (this.userGroups.pagination.current_page !== 1) {
        this.userGroups.pagination.current_page = 1
      } else {
        this.fetchUserGroups()
      }

      if (this.exercises.pagination.current_page !== 1) {
        this.exercises.pagination.current_page = 1
      } else {
        this.fetchAllExercises()
      }
    },
    async fetchAllExercises() {
      if (this.filters.groupId === 'groups') return

      if (this.exercises.loading) return

      this.exercises.loading = true

      try {
        const result = await TrainingExerciseApiService.get({
          group_id: this.filters.groupId,
          tags: this.filters.tags,
          search: this.filters.search,
          page: this.exercises.pagination.current_page,
          per_page: this.exercises.pagination.per_page,
          is_shared: this.filters.shareStatus === 'methodology',
          only_my_exercises: this.filters.onlyMyExercises || undefined
        })

        this.exercises.data = result.data
        this.exercises.pagination = result.meta
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_exercises.error'),
          color: 'error'
        })

        this.exercises.pagination.current_page = 1
        this.exercises.pagination.last_page = 1

        throw e
      } finally {
        this.exercises.loading = false
      }
    },
    async fetchAllGroups() {
      this.groups.loading = true

      try {
        this.groups.data = await TrainingExerciseApiService.getAllGroups()
        this.filters.groupId = this.groups.data[0]?.id
        this.filters.tags = {}
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_groups.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.groups.loading = false
      }
    },
    async fetchUserGroups() {
      if (this.filters.groupId !== 'groups') return

      if (this.userGroups.loading) return

      this.userGroups.loading = true

      try {
        const result = await TrainingExerciseApiService.getUserGroups({
          search: this.filters.search,
          page: this.userGroups.pagination.current_page,
          per_page: this.userGroups.pagination.per_page,
          tags: this.filters.groupTags
        })

        this.userGroups.data = result.data
        this.userGroups.pagination = result.meta
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_saved_exercise_groups.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.userGroups.loading = false
      }
    },
    assignInternalIds(items) {
      for (const item of items) {
        if (!item.internal_id) {
          item.internal_id = uniqueId()
        }
        if (item.exercises) this.assignInternalIds(item.exercises)
      }
    },
    addExerciseToList(exercise, forceLastSection = false) {
      exercise = cloneDeep(exercise)

      exercise.internal_id = uniqueId()
      exercise.configureParams = true
      exercise.type = 'exercise'
      exercise.exercise_id = exercise.id

      if (this.items.length !== 0) {
        const lastItem = this.items[this.items.length - 1]

        if ((lastItem.type === 'superset' || lastItem.type === 'group') && (lastItem.exercises.length === 0 || forceLastSection)) {
          lastItem.exercises.push(exercise)

          return
        }
      }

      this.items.push(exercise)
    },
    addSuperset() {
      this.items.push({
        id: 'superset' + (this.items.filter(e => e.type === 'superset').length + 1),
        internal_id: uniqueId(),
        type: 'superset',
        exercises: []
      })
    },
    addPause() {
      const pause = {
        id: 'pause' + (this.items.filter(e => e.type === 'pause').length + 1),
        internal_id: uniqueId(),
        type: 'pause'
      }

      if (this.items.length !== 0) {
        const lastItem = this.items[this.items.length - 1]

        if (lastItem.type === 'superset' || lastItem.type === 'group') {
          lastItem.exercises.push(pause)

          return
        }
      }

      this.items.push(pause)
    },
    addGroup() {
      this.items.push({
        id: 'group' + (this.items.filter(e => e.type === 'group').length + 1),
        internal_id: uniqueId(),
        type: 'group',
        params: {
          title: i18n.t('training_builder.section')
        },
        exercises: [],
        tags: [],
        configureParams: true
      })
    },
    addSavedGroup(savedGroup) {
      const group = {
        id: 'group' + (this.items.filter(e => e.type === 'group').length + 1),
        internal_id: uniqueId(),
        type: 'group',
        params: {
          title: savedGroup.title
        },
        userGroupId: savedGroup.id,
        tags: savedGroup.tags,
        exercises: cloneDeep(savedGroup.exercises)
      }

      this.items.push(group)
      this.assignInternalIds(group.exercises)
    },
    async submit() {
      if (this.isReadOnly) return

      if (!(await this.$refs.validation.validate())) return

      if (this.editItems.length > 0) {
        this.$emit('update:edit-items', this.items, {
          name: this.protocolFormData.name,
          description: this.protocolFormData.description
        })

        this.modalVisible = false
        return
      }

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          name: this.protocolFormData.name,
          description: this.protocolFormData.description,
          items: this.items,
          is_hidden: !this.listenEvents ? !this.saveAsTemplate : false, // hidden if created from event context and not saved as template
          is_shared: this.protocolFormData.is_shared
        }

        let result

        if (this.protocolFormData.id) {
          result = await TrainingProtocolApiService.update(this.protocolFormData.id, data)
        } else {
          result = await TrainingProtocolApiService.create(data)
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_protocol_save.success'),
          color: 'success'
        })

        this.$emit('refresh', result.data)

        if (this.protocolFormData.id) {
          EventBus.$emit(this.$events.TRAINING_PROTOCOL_UPDATED, result.data)
        } else {
          EventBus.$emit(this.$events.TRAINING_PROTOCOL_CREATED, result.data)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_protocol_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteProtocol() {
      if (!this.protocolFormData.id) return

      try {
        this.deleting = true

        await TrainingProtocolApiService.delete(this.protocolFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_protocol_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.TRAINING_PROTOCOL_DELETED, this.protocolFormData)
        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_protocol_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    checkDragDrop(event) {
      const draggedElementType = event?.draggedContext?.element?.type
      const dropComponentBelongsTo = event?.relatedContext?.component?.$attrs['belongs-to']

      if (draggedElementType === 'group' && dropComponentBelongsTo === 'superset') return false
      if (draggedElementType === 'group' && dropComponentBelongsTo === 'group') return false
      if (draggedElementType === 'superset' && dropComponentBelongsTo === 'superset') return false

      return true
    },
    handleFilterTag(tagId, tagValue, triggerFetch = true) {
      this.$set(this.filters.tags, tagId, !tagValue ? undefined : tagValue)

      const tagDefinitions = this.selectedGroup?.params?.tags

      if (!tagDefinitions) return

      const dependingTags = tagDefinitions.filter(tag => tag.filter_by === tagId)

      for (const tag of dependingTags) {
        if (this.filters.tags[tag.id]) {
          this.filters.tags[tag.id] = undefined
          this.handleFilterTag(tag.id, undefined, false)
        }
      }

      if (triggerFetch) {
        if (this.exercises.pagination.current_page !== 1) {
          this.exercises.pagination.current_page = 1
        } else {
          this.fetchAllExercises()
        }
      }
    },
    handleGroupFilter(groupId) {
      if (groupId === this.filters.groupId) return

      this.filters.tags = {}
      this.filters.groupId = groupId
    },
    createNewExercise() {
      EventBus.$emit(this.$events.OPEN_DIALOG_TRAINING_EXERCISE_FORM)
    },
    async fetchTags() {
      this.tags.loading = true

      try {
        const result = await TagApiService.getAll()
        this.tags.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_tags.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.tags.loading = false
      }
    },
    duplicateProtocol() {
      if (!this.protocolFormData.id) return

      this.protocolFormData.id = null

      this.protocolFormData.name = this.protocolFormData.name + ' copy'

      this.$store.dispatch(this.$storeActions.ALERT, {
        text: this.$t('alert.training_protocol_duplicated.success'),
        color: 'success'
      })
    }
  }
}
</script>
